import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { useToast } from "@shared/hooks/use-toast";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { useAddSopNote, useEditSopNote } from "../../../../hooks";
import { Loader2 } from "lucide-react";

interface Note {
  code: string;
  name: string;
  isActive: boolean;
  noteColor: string;
  createdDate?: string;
  noteDescription: string;
}

interface NoteFormData {
  code: string;
  name: string;
  isActive: boolean;
  noteColor: string;
  noteDescription: string;
}

interface AddNoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  sopId: string;
  remoteUtilityId: string;
}

interface EditNoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  sopId: string;
  note: Note | null;
  noteCode: string;
}

// Predefined color options
const NOTE_COLORS = [
  { value: "red", label: "Red", className: "bg-red-100 text-red-800" },
  { value: "blue", label: "Blue", className: "bg-blue-100 text-blue-800" },
  { value: "green", label: "Green", className: "bg-green-100 text-green-800" },
  {
    value: "yellow",
    label: "Yellow",
    className: "bg-yellow-100 text-yellow-800",
  },
  {
    value: "purple",
    label: "Purple",
    className: "bg-purple-100 text-purple-800",
  },
  {
    value: "orange",
    label: "Orange",
    className: "bg-orange-100 text-orange-800",
  },
  { value: "gray", label: "Gray", className: "bg-gray-100 text-gray-800" },
];

// Add Note Modal Component
export const AddNoteModal: React.FC<AddNoteModalProps> = ({
  isOpen,
  onClose,
  sopId,
  remoteUtilityId,
}) => {
  const { toast } = useToast();
  const addNoteMutation = useAddSopNote();

  // Transform color options for form
  const colorOptions = useMemo(
    () =>
      NOTE_COLORS.map((color) => ({
        value: color.value,
        label: color.label,
      })),
    []
  );

  // Form fields configuration
  const formFields: FormField[] = useMemo(
    () => [
      {
        name: "code",
        type: "text",
        label: "Code",
        placeholder: "Enter note code",
        required: true,
        minLength: 1,
        maxLength: 20,
        helperText: "Unique identifier for the note",
      },
      {
        name: "name",
        type: "text",
        label: "Name",
        placeholder: "Enter note name",
        required: true,
        minLength: 2,
        maxLength: 100,
        helperText: "Provide a clear name for the note",
      },
      {
        name: "noteColor",
        type: "select",
        label: "Color",
        placeholder: "Select note color",
        required: true,
        options: colorOptions,
        helperText: "Choose a color for the note",
      },
      {
        name: "noteDescription",
        type: "textarea",
        label: "Description",
        placeholder: "Enter note description",
        required: false,
        maxLength: 500,
        rows: 3,
        helperText: "Optional description for the note",
      },
      {
        name: "isActive",
        type: "switch",
        label: "Active",
        helperText: "Enable if this note should be active",
        defaultValue: true,
      },
    ],
    [colorOptions]
  );

  // Create form instance using FormService
  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: {
      code: "",
      name: "",
      isActive: true,
      noteColor: "blue",
      noteDescription: "",
    },
  });

  // Handle field changes
  const handleFieldChange = useCallback((fieldName: string, value: any) => {
    // Optional: Add any side effects here if needed
  }, []);

  // Handle form submission
  const handleSubmit = async (data: NoteFormData) => {
    try {
      await addNoteMutation.mutateAsync({
        sopId,
        remoteUtilityId,
        noteData: {
          code: data.code,
          name: data.name,
          is_active: data.isActive,
          note_color: data.noteColor,
          note_description: data.noteDescription,
        },
      });

      toast({
        title: "Success",
        description: "Note added successfully",
      });

      // Reset form and close modal
      FormService.resetForm(form, formFields);
      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add note",
        variant: "destructive",
      });
    }
  };

  const handleReset = useCallback(() => {
    FormService.resetForm(form, formFields);
  }, [form, formFields]);

  const handleClose = useCallback(() => {
    FormService.resetForm(form, formFields);
    onClose();
  }, [form, formFields, onClose]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      FormService.resetForm(form, formFields);
    }
  }, [isOpen, form, formFields]);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add New Note</DialogTitle>
          <DialogDescription>
            Add a new note to this SOP template
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 pr-4">
          <DynamicForm
            fields={formFields}
            form={form}
            onSubmit={handleSubmit}
            onReset={handleReset}
            onFieldChange={handleFieldChange}
            loading={addNoteMutation.isPending}
            gridColumns={1}
            config={{
              showResetButton: false,
              showSubmitButton: false,
              resetOnSubmit: false,
              classes: {
                form: "space-y-4",
                container: "space-y-2",
                label: "block text-sm font-medium text-gray-900 mb-1",
                input:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed",
                textarea:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed",
                select:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed",
                error: "text-sm text-red-600 mt-1",
                helperText: "text-xs text-muted-foreground mt-1",
                required: "text-red-500 ml-1",
              },
            }}
            renderCustomFooter={() => (
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={handleClose}
                  disabled={addNoteMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  onClick={form.handleSubmit(handleSubmit)}
                  disabled={addNoteMutation.isPending}
                >
                  {addNoteMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Adding...
                    </>
                  ) : (
                    "Add Note"
                  )}
                </Button>
              </DialogFooter>
            )}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Edit Note Modal Component
export const EditNoteModal: React.FC<EditNoteModalProps> = ({
  isOpen,
  onClose,
  sopId,
  note,
  noteCode,
}) => {
  const { toast } = useToast();
  const editNoteMutation = useEditSopNote();

  // Transform color options for form
  const colorOptions = useMemo(
    () =>
      NOTE_COLORS.map((color) => ({
        value: color.value,
        label: color.label,
      })),
    []
  );

  // Form fields configuration (same as Add modal)
  const formFields: FormField[] = useMemo(
    () => [
      {
        name: "code",
        type: "text",
        label: "Code",
        placeholder: "Enter note code",
        required: true,
        minLength: 1,
        maxLength: 20,
        helperText: "Unique identifier for the note",
      },
      {
        name: "name",
        type: "text",
        label: "Name",
        placeholder: "Enter note name",
        required: true,
        minLength: 2,
        maxLength: 100,
        helperText: "Provide a clear name for the note",
      },
      {
        name: "noteColor",
        type: "select",
        label: "Color",
        placeholder: "Select note color",
        required: true,
        options: colorOptions,
        helperText: "Choose a color for the note",
      },
      {
        name: "noteDescription",
        type: "textarea",
        label: "Description",
        placeholder: "Enter note description",
        required: false,
        maxLength: 500,
        rows: 3,
        helperText: "Optional description for the note",
      },
      {
        name: "isActive",
        type: "switch",
        label: "Active",
        helperText: "Enable if this note should be active",
        defaultValue: true,
      },
    ],
    [colorOptions]
  );

  // Create form instance using FormService
  const form = FormService.createForm({
    fields: formFields,
    mode: "onChange",
    validateOnMount: false,
    defaultValues: {
      code: "",
      name: "",
      isActive: true,
      noteColor: "blue",
      noteDescription: "",
    },
  });

  // Populate form when note changes
  useEffect(() => {
    if (note && isOpen) {
      const populateData = {
        code: note.code || "",
        name: note.name || "",
        isActive: note.isActive ?? true,
        noteColor: note.noteColor || "blue",
        noteDescription: note.noteDescription || "",
      };

      FormService.populateForm(form, populateData, formFields);
    }
  }, [note, isOpen, form, formFields]);

  // Handle field changes
  const handleFieldChange = useCallback((fieldName: string, value: any) => {
    // Optional: Add any side effects here if needed
  }, []);

  // Handle form submission
  const handleSubmit = async (data: NoteFormData) => {
    console.log("NOTE", data);
    console.log("SOID", sopId);
    if (!note) return;

    try {
      await editNoteMutation.mutateAsync({
        sopId,
        noteData: {
          code: data.code,
          name: data.name,
          is_active: data.isActive,
          note_color: data.noteColor,
          note_description: data.noteDescription,
        },
      });

      toast({
        title: "Success",
        description: "Note updated successfully",
      });

      onClose();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update note",
        variant: "destructive",
      });
    }
  };

  const handleReset = useCallback(() => {
    FormService.resetForm(form, formFields);
  }, [form, formFields]);

  const handleClose = useCallback(() => {
    FormService.resetForm(form, formFields);
    onClose();
  }, [form, formFields, onClose]);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit Note</DialogTitle>
          <DialogDescription>Update the note details</DialogDescription>
        </DialogHeader>

        <div className="space-y-6 pr-4">
          <DynamicForm
            fields={formFields}
            form={form}
            onSubmit={handleSubmit}
            onReset={handleReset}
            onFieldChange={handleFieldChange}
            loading={editNoteMutation.isPending}
            gridColumns={1}
            config={{
              showResetButton: false,
              showSubmitButton: false,
              resetOnSubmit: false,
              classes: {
                form: "space-y-4",
                container: "space-y-2", // ✅ Vertical stack
                label: "block text-sm font-medium text-gray-900 mb-1", // ✅ Block labels
                input:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed", // ✅ Full styling
                select:
                  "w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:bg-gray-100 disabled:cursor-not-allowed",
                error: "text-sm text-red-600 mt-1",
                helperText: "text-xs text-muted-foreground mt-1",
              },
            }}
            renderCustomFooter={() => (
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={handleClose}
                  disabled={editNoteMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  onClick={form.handleSubmit(handleSubmit)}
                  disabled={editNoteMutation.isPending}
                >
                  {editNoteMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Updating...
                    </>
                  ) : (
                    "Update Note"
                  )}
                </Button>
              </DialogFooter>
            )}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
